import React, { useEffect, useRef, createContext, useContext, useMemo, useCallback, useState } from "react";
import { message } from "antd";
import moment from "moment-timezone";
import { useSelector } from 'react-redux';
import PropTypes from "prop-types";
import { Device } from "@twilio/voice-sdk";
import { getCalltoken } from "../../../../Api/Services/ProductConfigApiService";
// import { getCallLogFromTwilio } from "../../../../Api/Services/CallLogApiService";

const CallContext = createContext(null);

export const useCall = () => useContext(CallContext);

function CallProvider({ children }) {
    const { CommApiValues } = useSelector((state) => state.callingRedux);
    const deviceRef = useRef(null);
    const callRef = useRef(null);
    const callEntityRef = useRef(null);
    const naCTriggerRef = useRef(null);
    const [isCalling, setIsCalling] = useState(false); // UI flag
    const [activeCallNumber, setActiveCallNumber] = useState(null);
    const [callStatus, setCallStatus] = useState(null);
    const [callStatusF, setCallStatusF] = useState(null);
    const [isMuted, setIsMuted] = useState(false);
    const [callEntityData, setCallEntityData] = useState()
    const [callLogData, setCallLogData] = useState()

    async function initTwilio() {
        try {
            if (deviceRef.current) return; // ✅ prevent re-init
            const Response = await getCalltoken();
            const device = new Device(Response.token, {
                logLevel: 1,
                codecPreferences: ["opus", "pcmu"],
            });

            device.onError = () => {
                message.error("Something went wrong. Please try again.");
            };

            // device.onIncoming = (call) => {
            //     callRef.current = call;
            //     call.onStateChange = (state) => {
            //         if (state === "ringing") message.info("📞 Incoming call...");
            //         if (state === "connected") {
            //             message.success("✅ Call connected");
            //             setIsCalling(true);
            //         }
            //         if (state === "disconnected") {
            //             message.warning("🔚 Call ended");
            //             setIsCalling(false);

            //             // reset call ref
            //             callRef.current = null;
            //         }
            //     };
            //     call.accept();
            // };

            deviceRef.current = device;
        } catch {
            message.error("Unable to connect. Please try again.");
        }
    }
    function extractTimeZoneName(tzString) {
        const parts = tzString.split(" ");
        return parts[1];
    }
    async function fetchCallLogFromTwilio() {
        try {
            const callbaceck = naCTriggerRef.current
            // const resposne = await getCallLogFromTwilio(callEntityRef?.current?.type === "Lead" ? callEntityRef?.current?.data?.leads_id : callEntityRef?.current?.data?.contacts_id, callEntityRef?.current?.type, callEntityRef?.current?.triggerNumber)
            setCallLogData(callEntityRef?.current)
            setCallStatusF("Active")
            callbaceck("triggers")
        } catch (error) {
            setCallLogData()
            message.error(error)
        }
    }
    const trackCall = useCallback((call) => {
        if (!call) return;

        callRef.current = call;
        setIsCalling(true);

        call.on("ringing", () => {
            setCallStatus("ringing");
        });

        call.on("accept", () => {
            setCallStatus("connected");
        });

        call.on("reconnecting", () => {
            setCallStatus("reconnecting");
        });

        call.on("reject", () => {
            message.info("Call ended");
            setCallStatus("rejected");
            if (callEntityRef.current && callEntityRef.current.type !== "User") {
                setCallLogData()
                fetchCallLogFromTwilio(callEntityRef.current)
            }
            setIsCalling(false);
            callRef.current = null;
        });

        call.on("cancel", () => {
            message.info("Call ended");
            setCallStatus("canceled");
            setIsCalling(false);
            if (callEntityRef.current && callEntityRef.current.type !== "User") {
                setCallLogData()
                fetchCallLogFromTwilio(callEntityRef.current)
            }
            callRef.current = null;
        });

        call.on("disconnect", () => {
            message.info("Call ended");
            setCallStatus("disconnected");
            if (callEntityRef.current && callEntityRef.current.type !== "User") {
                setCallLogData()
                fetchCallLogFromTwilio(callEntityRef.current)
            }
            setIsCalling(false);
            callRef.current = null;
        });
    }, []);

    const makeCall = useCallback(async (profile, to, EntityData, setNaCTrigger) => {
        if (!to) return;
        const device = deviceRef.current;
        if (!device) return message.error("Device not ready");
        try {
            const call = await device.connect({ params: { To: to } });
            const tzString = profile?.timeZone || "(GMT+0:00) UTC";
            const tz = extractTimeZoneName(tzString);
            const nowInProfile = moment().tz(tz);
            const date = nowInProfile.format("YYYY-MM-DD"); // YYYY-MM-DD
            const time = nowInProfile.format("HH:mm");      // HH:mm (24hr)
            setActiveCallNumber(to);
            setCallEntityData({
                ...EntityData,
                triggerNumber: to,
                triggeredDate: date, // YYYY-MM-DD
                triggeredTime: time  // HH:mm
            })
            trackCall(call);
            if (setNaCTrigger) {
                naCTriggerRef.current = setNaCTrigger;
            }
            callEntityRef.current = {
                ...EntityData,
                triggeredDate: date,
                triggerNumber: to,
                triggeredTime: time
            };
        } catch (err) {
            message.error("Unable to connect");
            setCallEntityData()
            if (EntityData && EntityData.type !== "User") {
                setCallStatusF("NoActive")
            }
            setIsCalling(false);
        }
    }, [trackCall]);

    const muteCall = useCallback(() => {
        if (callRef.current) {
            callRef.current.mute(true);
            setIsMuted(true);
        }
    }, []);

    const unmuteCall = useCallback(() => {
        if (callRef.current) {
            callRef.current.mute(false);
            setIsMuted(false);
        }
    }, []);

    const cancelCall = useCallback(() => {
        const call = callRef.current;
        if (call && typeof call.disconnect === "function") {
            call.disconnect();
        } else {
            message.info("No active call to cancel");
        }
        if (callEntityRef.current && callEntityRef.current.type !== "User") {
            setCallLogData()
            fetchCallLogFromTwilio(callEntityRef.current)
        }
        setIsCalling(false);
        callRef.current = null;
    }, []);
    useEffect(() => {
        if (CommApiValues?.communicationType === "Twilio") {
            initTwilio();
        }
        return () => {
            if (deviceRef.current) {
                deviceRef.current.destroy();
                deviceRef.current = null;
                message.info("Disconnected");
            }
        };
    }, [CommApiValues?.communicationType]);
    // ✅ Memoize context value
    // const contextValue = useMemo(() => ({ makeCall }), [makeCall]);
    const contextValue = useMemo(() => ({
        callLogData,
        makeCall,
        cancelCall,
        muteCall,
        unmuteCall,
        setCallStatusF,
        isCalling,
        isMuted,
        activeCallNumber,
        callStatus,
        callEntityData,
        callStatusF,
        triggerFromButton: naCTriggerRef.current,
    }), [makeCall, cancelCall, muteCall, unmuteCall, setCallStatusF, isCalling, isMuted, activeCallNumber, callStatus, callEntityData, callStatusF]);

    return (
        <CallContext.Provider value={contextValue}>
            {children}
        </CallContext.Provider>
    );
}

CallProvider.propTypes = {
    children: PropTypes.node,
};

export { CallProvider };
